import { VideoPlayer } from "~/components/VideoPlayer"
import { useEffect, useRef, useState } from "react"
import { CaretRightFilled } from "@ant-design/icons"
import { Image } from "antd"
import classNames from "classnames"
import {
  defaultPreviewFrameSize,
  getPreviewFrameSizeClassName,
  previewFrameClassName,
  type PreviewFrameSize,
} from "~/components/ResourcePreview/previewFrame"
import styles from "./index.css"

const playIconClassNameBySize: Record<PreviewFrameSize, string> = {
  large: styles.playIconLarge,
  medium: styles.playIconMedium,
  small: styles.playIconSmall,
}

const transparentPreviewImage =
  "data:image/gif;base64,R0lGODlhAQABAAD/ACwAAAAAAQABAAACADs="

const setVideoReferrerPolicy = (element: HTMLVideoElement | null) => {
  element?.setAttribute("referrerpolicy", "no-referrer")
}

type VideoPreviewProps = {
  startTime?: number
  endTime?: number
  src: string
  poster?: string
  alt?: string
  className?: string
  fit?: "contain" | "cover"
  muted?: boolean
  onMetadataLoaded?: (metadata: { height: number; width: number }) => void
  onPreviewOpen?: () => void
  preview?: boolean
  showPlayIcon?: boolean
  size?: PreviewFrameSize
  width?: number | string
}

export const VideoPreview = ({
  src,
  startTime,
  endTime,
  poster,
  alt = "视频预览",
  className,
  fit = "cover",
  muted = false,
  onMetadataLoaded,
  onPreviewOpen,
  preview: previewEnabled = true,
  showPlayIcon = true,
  size = defaultPreviewFrameSize,
  width,
}: VideoPreviewProps) => {
  const hasRange = startTime !== undefined && endTime !== undefined
  const previewPoster = hasRange ? undefined : poster
  const thumbnailRef = useRef<HTMLVideoElement | null>(null)
  const [previewOpen, setPreviewOpen] = useState(false)

  useEffect(() => {
    const video = thumbnailRef.current
    if (
      hasRange &&
      video &&
      video.readyState >= 1 &&
      startTime < video.duration
    ) {
      video.currentTime = startTime
    }
  }, [hasRange, src, startTime])
  const handlePreviewVisibleChange = (open: boolean) => {
    if (open) {
      onPreviewOpen?.()
    }

    setPreviewOpen(open)
  }
  const preview = {
    imageRender: () => (
      <VideoPlayer
        className={hasRange ? styles.clipPlayer : styles.video}
        controls
        startTime={startTime}
        endTime={endTime}
        muted={muted}
        poster={previewPoster}
        objectFit="contain"
        ref={setVideoReferrerPolicy}
        src={src}
      />
    ),
    actionsRender: () => null,
    onVisibleChange: handlePreviewVisibleChange,
  }
  const posterClassName = classNames(
    styles.poster,
    fit === "contain" && styles.posterContain,
  )

  return (
    <div
      className={classNames(
        previewFrameClassName,
        getPreviewFrameSizeClassName(size),
        className,
      )}
      style={{ width }}
    >
      {previewEnabled && !previewPoster ? (
        <Image
          classNames={{
            root: styles.hiddenPreview,
          }}
          preview={{
            ...preview,
            visible: previewOpen,
          }}
          src={transparentPreviewImage}
        />
      ) : null}
      {previewPoster ? (
        <Image
          alt={alt}
          className={posterClassName}
          height="100%"
          preview={previewEnabled ? preview : false}
          src={previewPoster}
          width="100%"
        />
      ) : (
        <VideoPlayer
          aria-label={alt}
          className={posterClassName}
          muted
          onLoadedMetadata={(event) => {
            const video = event.currentTarget
            if (hasRange && startTime < video.duration)
              video.currentTime = startTime
            const { videoHeight, videoWidth } = video
            if (videoWidth <= 0 || videoHeight <= 0) return
            onMetadataLoaded?.({ height: videoHeight, width: videoWidth })
          }}
          onClick={(event) => {
            if (previewEnabled) {
              event.preventDefault()
              event.stopPropagation()
              handlePreviewVisibleChange(true)
            }
          }}
          playsInline
          preload="metadata"
          ref={(element) => {
            thumbnailRef.current = element
            setVideoReferrerPolicy(element)
          }}
          src={src}
        />
      )}
      {showPlayIcon && previewPoster && (
        <CaretRightFilled
          className={classNames(styles.playIcon, playIconClassNameBySize[size])}
        />
      )}
    </div>
  )
}
